import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PasswordStrengthIndicator = ({ password, onValidationChange }) => {
  const [strength, setStrength] = useState({
    valid: false,
    strength: 'none',
    score: 0,
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecial: false
    },
    errors: []
  });

  useEffect(() => {
    const checkStrength = async () => {
      if (!password) {
        setStrength({
          valid: false,
          strength: 'none',
          score: 0,
          requirements: {
            minLength: false,
            hasUppercase: false,
            hasLowercase: false,
            hasNumber: false,
            hasSpecial: false
          },
          errors: []
        });
        if (onValidationChange) onValidationChange(false);
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/check-password-strength`, {
          password
        });
        setStrength(response.data);
        if (onValidationChange) onValidationChange(response.data.valid);
      } catch (error) {
        console.error('Error checking password strength:', error);
      }
    };

    const timeoutId = setTimeout(checkStrength, 300);
    return () => clearTimeout(timeoutId);
  }, [password, onValidationChange]);

  if (!password) return null;

  const getStrengthColor = () => {
    switch (strength.strength) {
      case 'strong': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'weak': return 'bg-orange-500';
      default: return 'bg-red-500';
    }
  };

  const getStrengthText = () => {
    switch (strength.strength) {
      case 'strong': return 'Strong';
      case 'medium': return 'Medium';
      case 'weak': return 'Weak';
      default: return 'Very Weak';
    }
  };

  const getStrengthTextColor = () => {
    switch (strength.strength) {
      case 'strong': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'weak': return 'text-orange-600';
      default: return 'text-red-600';
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {/* Strength Bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: `${(strength.score / 5) * 100}%` }}
          />
        </div>
        <span className={`text-sm font-medium ${getStrengthTextColor()}`}>
          {getStrengthText()}
        </span>
      </div>

      {/* Requirements Checklist */}
      <div className="text-xs space-y-1">
        <div className={strength.requirements.minLength ? 'text-green-600' : 'text-gray-500'}>
          {strength.requirements.minLength ? '✓' : '○'} At least 8 characters
        </div>
        <div className={strength.requirements.hasUppercase ? 'text-green-600' : 'text-gray-500'}>
          {strength.requirements.hasUppercase ? '✓' : '○'} Contains uppercase letter (A-Z)
        </div>
        <div className={strength.requirements.hasLowercase ? 'text-green-600' : 'text-gray-500'}>
          {strength.requirements.hasLowercase ? '✓' : '○'} Contains lowercase letter (a-z)
        </div>
        <div className={strength.requirements.hasNumber ? 'text-green-600' : 'text-gray-500'}>
          {strength.requirements.hasNumber ? '✓' : '○'} Contains number (0-9)
        </div>
        <div className={strength.requirements.hasSpecial ? 'text-green-600' : 'text-gray-500'}>
          {strength.requirements.hasSpecial ? '✓' : '○'} Contains special character (!@#$%^&*)
        </div>
      </div>

      {/* Error Messages */}
      {strength.errors.length > 0 && (
        <div className="text-xs text-red-600 mt-2">
          {strength.errors.map((error, index) => (
            <div key={index}>• {error}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;
